import React, { useState } from 'react';
import { Upload, FileText, Copy, ArrowLeft, AlertTriangle, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import domainConfig from './config/domainConfig';
import { generateStandardizedPDF, REPORT_SECTIONS } from './utils/pdfGenerator';
import ReportHeader from './components/ReportHeader';

const VanGoFinalDownsReportGenerator = () => {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearAll = () => {
    setFile(null);
    setReport(null);
    setLoading(false);
    setError(null);
  };

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    // Security validations - Accept Excel files
    const maxFileSize = 10 * 1024 * 1024; // 10MB limit
    const allowedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    const allowedExtensions = ['.xlsx', '.xls'];
    
    if (uploadedFile.size > maxFileSize) {
      setError('File size must be less than 10MB');
      return;
    }

    const fileExtension = uploadedFile.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (!allowedExtensions.includes(fileExtension) && !allowedTypes.includes(uploadedFile.type)) {
      if (!uploadedFile.name.toLowerCase().match(/\.(xlsx|xls)$/)) {
        setError('Please upload an Excel file only (.xlsx or .xls)');
        return;
      }
    }

    setFile(uploadedFile);
    setError(null);
  };

  const processData = async () => {
    if (!file) return;
    
    setLoading(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('excelFile', file);

      // Send file to server for processing
      const response = await fetch(`${domainConfig.apiUrl}/api/reports/vangofinaldowns`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate report');
      }

      const result = await response.json();
      setReport(result.report);
    } catch (err) {
      setError('Error processing file: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTendencyFlag = (percentage) => {
    const pct = parseFloat(percentage);
    if (pct >= 80) return ' ❗ EXTREMELY VALUABLE';
    if (pct >= 70) return ' ⚠️ VALUABLE';
    return '';
  };

  const generateReportText = () => {
    if (!report) return '';

    let text = `🎯 VANGO TRUE FINAL DOWNS ANALYSIS REPORT\n`;
    text += `Generated on ${new Date().toLocaleDateString()}\n\n`;

    if (report.logos?.clientLogo || report.logos?.companyLogo) {
      text += `📋 REPORT DETAILS\n`;
      if (report.logos?.clientLogo) text += `Client: [Logo included]\n`;
      if (report.logos?.companyLogo) text += `Analysis by: [Company logo included]\n`;
      text += `\n`;
    }

    text += `📊 DATA VERIFICATION - TRUE FINAL DOWNS FILTER\n`;
    text += `Using file: ${file?.name}\n`;
    text += `Raw 3rd downs: ${report.verification?.raw3rdDowns}\n`;
    text += `3rd downs excluded (followed by 4th): ${report.verification?.excluded3rdDowns}\n`;
    text += `True 3rd downs included: ${report.verification?.true3rdDowns}\n`;
    text += `All 4th downs: ${report.verification?.all4thDowns}\n`;
    text += `TOTAL TRUE FINAL DOWNS: ${report.verification?.totalTrueFinalDowns}\n\n`;

    text += `📈 FOUNDATION DATA\n`;
    text += `Total Runs: ${report.foundation?.totalRuns} (${report.foundation?.runPercentage}%)\n`;
    text += `Total True Pass Attempts: ${report.foundation?.totalPasses} (${report.foundation?.passPercentage}%)\n`;
    text += `All Pass Plays (including INT/SAC/RUN/LAT): ${report.foundation?.allPassPlays}\n`;
    text += `Pass Completions: ${report.foundation?.completions} of ${report.foundation?.totalPasses} (${report.foundation?.completionRate}%)\n\n`;

    if (report.distanceAnalysis) {
      text += `📏 DISTANCE BREAKDOWN\n`;
      report.distanceAnalysis.forEach((distance) => {
        text += `${distance.range} (${distance.totalPlays} plays):\n`;
        text += `  3rd downs: ${distance.thirdDowns} | 4th downs: ${distance.fourthDowns}\n`;
        text += `  Runs: ${distance.runs} (${distance.runPercentage}%${getTendencyFlag(distance.runPercentage)})\n`;
        text += `  All Pass Plays: ${distance.allPasses} (${distance.passPercentage}%${getTendencyFlag(distance.passPercentage)})\n`;
        text += `  True Pass Attempts (COM+INC): ${distance.truePassAttempts}\n`;
        text += `  Pass Completions: ${distance.completions} of ${distance.truePassAttempts} (${distance.completionRate}%)\n`;
        text += `  Average Pass Depth: ${distance.avgPassDepth} yards\n\n`;
      });
    }

    if (report.passTargets) {
      text += `🎯 PASS TARGET ANALYSIS\n`;
      report.passTargets.forEach((target) => {
        text += `${target.receiver}: ${target.totalTargets} total targets (${target.targetShare}% of true attempts), `;
        text += `${target.completions}/${target.attempts} true attempts (${target.completionRate}%), `;
        text += `${target.avgDepth} avg depth\n`;
      });
      text += `\n`;
    }

    if (report.keyInsights) {
      text += `🔍 KEY INSIGHTS\n`;
      report.keyInsights.forEach(insight => {
        text += `• ${insight}\n`;
      });
      text += `\n`;
    }

    if (report.criticalTendencies) {
      text += `🚨 CRITICAL TENDENCIES\n`;
      
      if (report.criticalTendencies.extremely?.length > 0) {
        text += `Extremely Valuable Tendencies (≥80%) ❗\n`;
        report.criticalTendencies.extremely.forEach(tendency => {
          text += `• ${tendency}\n`;
        });
        text += `\n`;
      }

      if (report.criticalTendencies.valuable?.length > 0) {
        text += `Valuable Tendencies (70-79%) ⚠️\n`;
        report.criticalTendencies.valuable.forEach(tendency => {
          text += `• ${tendency}\n`;
        });
      }
    }

    return text;
  };

  const copyReport = async () => {
    if (!report) return;

    const reportText = generateReportText();
    
    try {
      await navigator.clipboard.writeText(reportText);
      showSuccessMessage('Report copied to clipboard!');
    } catch (err) {
      setError('Failed to copy to clipboard. Please try again.');
    }
  };

  const showSuccessMessage = (message) => {
    const originalError = error;
    setError(null);
    
    const alertDiv = document.createElement('div');
    alertDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4caf50;
      color: white;
      padding: 15px 20px;
      border-radius: 5px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    `;
    alertDiv.textContent = message;
    document.body.appendChild(alertDiv);
    
    setTimeout(() => {
      document.body.removeChild(alertDiv);
      setError(originalError);
    }, 3000);
  };

  const renderReport = () => {
    if (!report) return null;

    return (
      <Box sx={{ mt: 2, bgcolor: 'white', minHeight: '100vh' }}>
        {/* Header Section - Using Shared Component */}
        <ReportHeader 
          title="VANGO TRUE FINAL DOWNS ANALYSIS"
          logos={report.logos}
          fileName={file?.name}
        />

        {/* Report Content */}
        <Box sx={{ p: 3 }}>

        {/* Data Verification Section */}
        <Box sx={{ mb: 4 }} data-section="vango-final-verification-section">
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
            DATA VERIFICATION - TRUE FINAL DOWNS FILTER
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#f8f9fa'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.verification?.raw3rdDowns || 0}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  RAW 3RD DOWNS
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#ffebee'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.verification?.excluded3rdDowns || 0}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  3RD DOWNS EXCLUDED
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#e8f5e8'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.verification?.true3rdDowns || 0}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  TRUE 3RD DOWNS
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#e3f2fd'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.verification?.all4thDowns || 0}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  ALL 4TH DOWNS
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ 
                border: '2px solid #000', 
                p: 2, 
                textAlign: 'center',
                bgcolor: '#f3e5f5'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.verification?.totalTrueFinalDowns || 0}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                  TOTAL TRUE FINAL
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Foundation Data Section */}
        <Box sx={{ mb: 4 }} data-section="vango-final-foundation-section">
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
            FOUNDATION DATA
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Box sx={{ border: '1px solid #000', p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.foundation?.totalRuns || 0}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  TOTAL RUNS ({report.foundation?.runPercentage || 0}%)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ border: '1px solid #000', p: 2, textAlign: 'center', bgcolor: '#f0f8ff' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.foundation?.totalPasses || 0}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  TRUE PASS ATTEMPTS ({report.foundation?.passPercentage || 0}%)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ border: '1px solid #000', p: 2, textAlign: 'center', bgcolor: '#fff8f0' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.foundation?.allPassPlays || 0}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  ALL PASS PLAYS
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ border: '1px solid #000', p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {report.foundation?.completionRate || 0}%
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  COMPLETION RATE ({report.foundation?.completions}/{report.foundation?.totalPasses})
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Distance Analysis Section */}
        {report.distanceAnalysis && (
          <Box sx={{ mb: 4 }} data-section="vango-final-distance-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              DISTANCE BREAKDOWN & ANALYSIS
            </Typography>
            
            {report.distanceAnalysis.map((distance, index) => (
              <Box key={index} sx={{ mb: 3 }} data-section={`vango-final-distance-${index}`}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 2, bgcolor: '#e9ecef', p: 1, border: '1px solid #000' }}>
                  {distance.range} ({distance.totalPlays} plays)
                </Typography>
                
                {/* Distance Summary Stats */}
                <Grid container spacing={0} sx={{ mb: 2 }}>
                  <Grid item xs={2.4}>
                    <Box sx={{ 
                      border: '1px solid #000', 
                      p: 1.5, 
                      textAlign: 'center',
                      bgcolor: '#f8f9fa'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000' }}>
                        {distance.totalPlays}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        TOTAL PLAYS
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={2.4}>
                    <Box sx={{ 
                      border: '1px solid #000', 
                      p: 1.5, 
                      textAlign: 'center',
                      bgcolor: parseFloat(distance.runPercentage) >= 70 ? '#ffebee' : '#f8f9fa'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000' }}>
                        {distance.runs}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        RUNS ({distance.runPercentage}%)
                        {parseFloat(distance.runPercentage) >= 80 && ' 🚨'}
                        {parseFloat(distance.runPercentage) >= 70 && parseFloat(distance.runPercentage) < 80 && ' ⚠️'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={2.4}>
                    <Box sx={{ 
                      border: '1px solid #000', 
                      p: 1.5, 
                      textAlign: 'center',
                      bgcolor: parseFloat(distance.passPercentage) >= 70 ? '#e3f2fd' : '#f8f9fa'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000' }}>
                        {distance.allPasses}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        ALL PASSES ({distance.passPercentage}%)
                        {parseFloat(distance.passPercentage) >= 80 && ' 🚨'}
                        {parseFloat(distance.passPercentage) >= 70 && parseFloat(distance.passPercentage) < 80 && ' ⚠️'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={2.4}>
                    <Box sx={{ 
                      border: '1px solid #000', 
                      p: 1.5, 
                      textAlign: 'center',
                      bgcolor: '#f0f8ff'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000' }}>
                        {distance.completionRate}%
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        COMPLETION ({distance.completions}/{distance.truePassAttempts})
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={2.4}>
                    <Box sx={{ 
                      border: '1px solid #000', 
                      p: 1.5, 
                      textAlign: 'center',
                      bgcolor: '#fff8f0'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000' }}>
                        {distance.avgPassDepth}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        AVG PASS DEPTH
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Typography variant="body2" sx={{ color: '#666', pl: 1 }}>
                  3rd downs: {distance.thirdDowns} | 4th downs: {distance.fourthDowns}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Pass Target Analysis */}
        {report.passTargets?.length > 0 && (
          <Box sx={{ mb: 4 }} data-section="vango-final-targets-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              PASS TARGET ANALYSIS
            </Typography>
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '2px solid #000' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#e9ecef' }}>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>RECEIVER</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>TOTAL TARGETS</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>TARGET SHARE</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>COMPLETION RATE</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', border: '1px solid #000' }}>AVG DEPTH</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.passTargets.map((target, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem', border: '1px solid #000' }}>
                        {target.receiver}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.9rem', border: '1px solid #000' }}>
                        {target.totalTargets}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.9rem', border: '1px solid #000' }}>
                        {target.targetShare}%
                      </TableCell>
                      <TableCell align="center" sx={{ 
                        fontSize: '0.9rem', 
                        border: '1px solid #000',
                        fontWeight: parseFloat(target.completionRate) >= 70 ? 'bold' : 'normal',
                        bgcolor: parseFloat(target.completionRate) >= 80 ? '#e8f5e8' : 
                                 parseFloat(target.completionRate) >= 70 ? '#fff3e0' : 'inherit'
                      }}>
                        {target.completionRate}% ({target.completions}/{target.attempts})
                        {parseFloat(target.completionRate) >= 80 && ' 🚨'}
                        {parseFloat(target.completionRate) >= 70 && parseFloat(target.completionRate) < 80 && ' ⚠️'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.9rem', border: '1px solid #000' }}>
                        {target.avgDepth} yards
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Key Insights */}
        {report.keyInsights?.length > 0 && (
          <Box sx={{ mb: 4 }} data-section="vango-final-insights-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              KEY INSIGHTS
            </Typography>
            <Box sx={{ pl: 2 }}>
              {report.keyInsights.map((insight, index) => (
                <Box key={index} sx={{ 
                  p: 1, 
                  mb: 1, 
                  bgcolor: '#f8f9fa', 
                  border: '1px solid #ddd',
                  borderRadius: 1
                }}>
                  <Typography variant="body1">
                    • {insight}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Critical Tendencies */}
        {report.criticalTendencies && (report.criticalTendencies.extremely?.length > 0 || report.criticalTendencies.valuable?.length > 0) && (
          <Box sx={{ mb: 4 }} data-section="vango-final-tendencies-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              CRITICAL TENDENCIES
            </Typography>
            
            {report.criticalTendencies.extremely?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f', mb: 2, bgcolor: '#ffebee', p: 1, border: '1px solid #d32f2f' }}>
                  🚨 EXTREMELY VALUABLE TENDENCIES (≥80%)
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {report.criticalTendencies.extremely.map((tendency, index) => (
                    <Box key={index} sx={{ 
                      p: 1, 
                      mb: 1, 
                      bgcolor: '#ffcdd2', 
                      border: '1px solid #d32f2f',
                      borderRadius: 1
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        • {tendency}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {report.criticalTendencies.valuable?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 2, bgcolor: '#fff3e0', p: 1, border: '1px solid #f57c00' }}>
                  ⚠️ VALUABLE TENDENCIES (70-79%)
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {report.criticalTendencies.valuable.map((tendency, index) => (
                    <Box key={index} sx={{ 
                      p: 1, 
                      mb: 1, 
                      bgcolor: '#ffe0b2', 
                      border: '1px solid #f57c00',
                      borderRadius: 1
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        • {tendency}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
        
        </Box> {/* End Report Content */}
      </Box>
    );
  };

  const saveReportAsPDF = async () => {
    await generateStandardizedPDF(
      REPORT_SECTIONS.VANGO_FINAL_DOWNS,
      'vango-final-downs-report.pdf',
      setError
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          component={Link}
          to="/reports"
          variant="outlined"
          startIcon={<ArrowLeft size={16} />}
        >
          Back to Reports
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Target size={32} />
        VanGo True Final Downs Analysis Report
      </Typography>

      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Upload your Excel file containing 3rd and 4th down data to generate a comprehensive true final downs analysis. 
        Analyzes only situations where the defense knows the offense MUST convert or give up possession.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* File Upload Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Upload size={20} />
            Upload Excel File
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select an Excel file (.xlsx or .xls) containing final downs data with columns: Dn, Dist, R/P, PaRes, pff_PASSWRTAR, PER, FINAL FORM, OFF PLAY. 
            The analysis will filter for true final downs and exclude 3rd downs followed by 4th down attempts.
          </Typography>

          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="excel-file-upload"
            disabled={loading}
          />
          <label htmlFor="excel-file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Upload size={16} />}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              Choose Excel File
            </Button>
          </label>

          {file && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Selected file:</strong> {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={processData}
          disabled={!file || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <FileText />}
          size="large"
        >
          {loading ? 'Generating Report...' : 'Generate True Final Downs Report'}
        </Button>
        
        <Button
          variant="outlined"
          onClick={clearAll}
          disabled={loading}
        >
          Clear All
        </Button>
      </Box>

      {loading && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="h6">Processing your Excel data...</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              The server is analyzing your Excel data and generating the true final downs report. This may take a few moments.
            </Typography>
          </CardContent>
        </Card>
      )}

      {report && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>Generated Report</Typography>
          
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={copyReport}
              startIcon={<Copy />}
            >
              Copy Report
            </Button>
            <Button
              variant="outlined"
              onClick={saveReportAsPDF}
            >
              Save as PDF
            </Button>
          </Box>

          <Box id="styled-report-content">
            {renderReport()}
          </Box>
        </Box>
      )}

      {/* Instructions */}
      <Card sx={{ mt: 4, bgcolor: 'info.light' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AlertTriangle size={20} />
            True Final Downs Methodology
          </Typography>
          <Typography variant="body2" component="div">
            <strong>What are "True Final Downs"?</strong>
            <p>Situations where the defense knows the offense MUST convert or give up possession. This excludes 3rd downs followed by 4th down attempts because they weren't truly "final" - the offense had another opportunity.</p>
            
            <strong>Required Excel Columns:</strong>
            <ul>
              <li><strong>Dn:</strong> Down number (0,1=1st, 2,22=2nd, 3=3rd, 4=4th)</li>
              <li><strong>Dist:</strong> Distance to first down</li>
              <li><strong>R/P:</strong> Run/Pass indicator (R=run, P=pass, X=excluded)</li>
              <li><strong>PaRes:</strong> Pass Result (COM=complete, INC=incomplete, INT, SAC)</li>
              <li><strong>pff_PASSWRTAR:</strong> Pass target receiver</li>
              <li><strong>PER:</strong> Personnel groupings</li>
            </ul>
            
            <strong>Analysis Process:</strong>
            <ol>
              <li>Filter for 3rd and 4th down plays only</li>
              <li>Identify 3rd downs followed by 4th downs (exclude these 3rd downs)</li>
              <li>Analyze remaining "true final downs" for tendencies</li>
              <li>Calculate completion rates using only COM+INC (true pass attempts)</li>
              <li>Flag critical tendencies (⚠️ = 70%+, ❗ = 80%+)</li>
            </ol>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VanGoFinalDownsReportGenerator;